/* eslint-disable consistent-return */
import {useMemo, useState} from 'react';

import {models} from 'powerbi-client';
import {oneOfType, node, func} from 'prop-types';

import {API_ENDPOINTS, DEFAULT_PROJECTS_SCHEMAS, EXPORTED_REPORT_FILE_TYPES, SNACKBAR_ACTIONS} from '../const';
import ReportContext from '../contexts/ReportContext';
import useHttp from '../hooks/misc/useHttp';
import useSnackbar from '../hooks/providers/useSnackbar';

const ReportProvider = ({children}) => {
  const [reports, setReports] = useState([]);
  const [reportsFetchInProgress, setReportsFetchInProgress] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedReportId, setSelectedReportId] = useState(null); // Hack to prevent object from mutating for header select
  const [embedToken, setEmbedToken] = useState(null);
  const [isTokenError, setIsTokenError] = useState(false);
  const [tokenExpiration, setTokenExpiration] = useState(Date.now());
  const [userCanUploadVisuals, setUserCanUploadVisuals] = useState(false);
  const [reportDataSuccessfullyUpdated, setReportDataSuccessfullyUpdated] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isReportInEditionMode, setIsReportInEditionMode] = useState(false);
  const [isEditableReportSaveAsModalOpen, setIsEditableReportSaveAsModalOpen] = useState(false);
  const [reportSelectedPage, setReportSelectedPage] = useState({});
  const [reportModule, setReportModule] = useState(null);
  const [isSaveReportModalOpen, setIsSaveReportModalOpen] = useState(false);
  const [isReportExportTypeChoiceModalOpen, setIsReportExportTypeChoiceModalOpen] = useState(false);

  const [isLoadingHidden, setIsLoadingHidden] = useState(false);
  const [isSlicerLoading, setIsSlicerLoading] = useState(true);

  const [shouldRegenerateToken, setShouldRegenerateToken] = useState(false);
  const [numberOfFailedReportsFetching, setNumberOfFailedReportsFetching] = useState(0);
  const [isAccountantDocumentRequestModalOpen, setAccountantDocumentRequestModalOpen] = useState(false);
  const [isReportLoaded, setIsReportLoaded] = useState(false);
  const [noProjectsSelectedBackdropOpen, setNoProjectsSelectedBackdropOpen] = useState(false);
  const [allReportVisuals, setAllReportVisuals] = useState([]);
  const [reportSelectedFoldersSirens, setReportSelectedFoldersSirens] = useState([]);
  const [canRefreshReport, setCanRefreshReport] = useState(true);
  const [shouldTestReportDatasourceConnections, setShouldTestReportDatasourceConnections] = useState(false);

  console.log({shouldTestReportDatasourceConnections});

  const {_post, _get} = useHttp();
  const {showSnackbar} = useSnackbar();

  const getAllReportVisuals = async () => {
    let visuals = [];
    const allPages = await selectedReport.getPages();
    // eslint-disable-next-line no-restricted-syntax
    for (const page of allPages) {
      // eslint-disable-next-line no-await-in-loop
      const pageVisuals = await page.getVisuals();
      visuals = [...visuals, ...pageVisuals];
    }
    return visuals;
  };

  // eslint-disable-next-line complexity
  const loadReports = async () => {
    if (reportsFetchInProgress) return;
    setReportsFetchInProgress(true);

    const url = API_ENDPOINTS.reports.getToken;
    const {response: tokenResponse, responseJson: tokenJson} = await _get(url);

    if (!tokenResponse.ok) {
      if (tokenResponse.status !== 401) {
        // eslint-disable-next-line no-console
        console.error('failed to get embed token from python');
      }

      setReportsFetchInProgress(false);

      // Business rule, 400 means user imported a report that contains RLS but without giving necessary information on RLS in import form
      if (tokenResponse.status === 400) {
        setIsTokenError(false);
        setEmbedToken(tokenJson.token);
        setTokenExpiration(tokenJson.expiration);
        setReports((tokenJson.reports || []).sort((a, b) => a.report_name.localeCompare(b.report_name)));
      }

      // Business rule, we will fetch reports + token up to 4 times if API returns a 404
      if (tokenResponse.status === 404 && numberOfFailedReportsFetching <= 4) {
        setNumberOfFailedReportsFetching(currentNumber => currentNumber + 1);
        setTimeout(() => setShouldRegenerateToken(true), 10000);
      }

      return {success: false};
    }

    try {
      if (tokenJson && tokenResponse.status === 200) {
        setIsTokenError(false);
        setEmbedToken(tokenJson.token);
        setReports(tokenJson.reports.sort((a, b) => a.report_name.localeCompare(b.report_name)));
        setTokenExpiration(tokenJson.expiration);
        setReportsFetchInProgress(false);
        setShouldTestReportDatasourceConnections(true);
        return {success: true, tokenData: tokenJson};
      }
      if (tokenJson && tokenResponse.status === 201) {
        setReports([]);
        setReportsFetchInProgress(false);
        return {success: false};
      }
    } catch (_e) {
      setIsTokenError(true);
      setReportsFetchInProgress(false);
      return {success: false};
    }
  };

  const saveAsReport = async (workspaceId, schema, reportName) => {
    const url = API_ENDPOINTS.reports.saveAs;
    try {
      const {response} = await _post(url, {
        workspace_id: workspaceId,
        schema,
        report_name: reportName
      });

      if (response.status === 200) {
        showSnackbar(SNACKBAR_ACTIONS.REPORT_SUCCESSFULLY_SAVED);
      }

      return {status: response.status};
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {success: false};
    }
  };

  const sendAccountantDocumentRequest = async ({siren, data, userId, userEmail}) => {
    const url = API_ENDPOINTS.reports.sendDocumentRequest;
    try {
      const {response} = await _post(url, {
        schema: DEFAULT_PROJECTS_SCHEMAS.gestion,
        siren,
        content: data,
        ...(userId && {to_user_id: userId}),
        ...(userEmail && {to_usermail: userEmail})
      });

      if (response.status === 200) {
        showSnackbar(SNACKBAR_ACTIONS.SEND_DOCUMENT_REQUEST_SUCCESS);
      }

      return {data, success: response.status === 200, status: response.status};
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {success: false};
    }
  };

  const exportReportDataToFile = async (reportName, visuals, exportType) => {
    const url = API_ENDPOINTS.reports.exportReportToFile;

    try {
      let reportContent;
      if (exportType === EXPORTED_REPORT_FILE_TYPES.POWERPOINT_NATIVE) {
        const activePage = await selectedReport.getActivePage();
        reportContent = {
          report_name: reportName,
          report_pages: visuals,
          report_colors: [
            import.meta.env.VITE_REPORT_COLOR_1,
            import.meta.env.VITE_REPORT_COLOR_2,
            import.meta.env.VITE_REPORT_COLOR_3,
            import.meta.env.VITE_REPORT_COLOR_4,
            import.meta.env.VITE_REPORT_COLOR_5,
            import.meta.env.VITE_REPORT_COLOR_6,
            import.meta.env.VITE_REPORT_COLOR_7,
            import.meta.env.VITE_REPORT_COLOR_8
          ],
          report_pixel_size: {
            width: activePage.defaultSize?.width,
            height: activePage.defaultSize?.height
          }
        };
      } else {
        const capturedBookmark = await selectedReport.bookmarksManager.capture();

        reportContent = {
          report_id: selectedReport.config.id,
          bookmarkState: capturedBookmark.state,
          sirens: reportSelectedFoldersSirens
        };
      }
      const {response, responseJson: data} = await _post(url, {
        export_type: exportType,
        content: reportContent
      });

      if (response.status === 200) {
        return {
          success: true,
          data
        };
      }
      // TODO, do not throw anymore
      throw Error(data?.message || data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {success: false};
    }
  };

  const getReportsListForProject = project => {
    if (!project) return [];

    const reportsList = reports.filter(r => !r.isCustomModel).filter(r => r.schema_name.includes(project.schema_name));
    return reportsList;
  };

  const updateReportDatasources = (reportId, newSources) => {
    console.log({reportId, newSources});
    setReports(current =>
      current.map(report => {
        if (report.report_id !== reportId) {
          return report;
        }

        return {
          ...report,
          gateway_datasources: newSources
        };
      })
    );
  };

  const updateGatewayDatasources = results => {
    const lookup = new Map();
    results.forEach(({id, code, message}) => {
      lookup.set(id, {
        is_connected: {
          code,
          message
        }
      });
    });

    setReports(current =>
      current.map(report => {
        let hasChanges = false;

        const updatedDatasources = report.gateway_datasources.map(ds => {
          const patch = lookup.get(ds.id);

          if (!patch) return ds;

          hasChanges = true;
          return {...ds, ...patch};
        });

        // Avoid unnecessary re-render if nothing changed in this report
        if (!hasChanges) return report;

        return {
          ...report,
          gateway_datasources: updatedDatasources
        };
      })
    );
  };
  const addReportRefresh = (reportId, newHistory) => {
    const MAX_REFRESHES = 5;

    setReports(current =>
      current.map(report => {
        if (report.report_id !== reportId) {
          return report;
        }

        return {
          ...report,

          refreshes: [newHistory, ...(report.refreshes || [])].slice(0, MAX_REFRESHES)
        };
      })
    );
  };

  const addReportScheduledUpdate = (reportId, newScheduleUpdate) => {
    setReports(current =>
      current.map(report => {
        if (report.report_id !== reportId) {
          return report;
        }

        return {
          ...report,
          refreshSchedule: newScheduleUpdate
        };
      })
    );
  };

  const isReportEditable = reports?.find(r => r.report_id === selectedReportId)?.isEditable || false;
  const isDesktopLayout = selectedReport?.config.settings.layoutType === models.LayoutType.Master;

  const value = useMemo(
    () => ({
      reports,
      setReports,
      selectedReport,
      setSelectedReport,
      selectedReportId,
      setSelectedReportId,
      embedToken,
      loadReports,
      isTokenError,
      tokenExpiration,
      setTokenExpiration,
      saveAsReport,
      userCanUploadVisuals,
      setUserCanUploadVisuals,
      reportDataSuccessfullyUpdated,
      setReportDataSuccessfullyUpdated,
      selectedProject,
      setSelectedProject,
      isReportInEditionMode,
      setIsReportInEditionMode,
      isEditableReportSaveAsModalOpen,
      setIsEditableReportSaveAsModalOpen,
      reportSelectedPage,
      setReportSelectedPage,
      reportModule,
      setReportModule,
      isReportEditable,
      isDesktopLayout,
      sendAccountantDocumentRequest,
      shouldRegenerateToken,
      setShouldRegenerateToken,
      isSaveReportModalOpen,
      setIsSaveReportModalOpen,
      isAccountantDocumentRequestModalOpen,
      setAccountantDocumentRequestModalOpen,
      isReportLoaded,
      setIsReportLoaded,
      noProjectsSelectedBackdropOpen,
      setNoProjectsSelectedBackdropOpen,
      isReportExportTypeChoiceModalOpen,
      setIsReportExportTypeChoiceModalOpen,
      allReportVisuals,
      setAllReportVisuals,
      getAllReportVisuals,
      exportReportDataToFile,
      setReportSelectedFoldersSirens,
      reportSelectedFoldersSirens,
      canRefreshReport,
      setCanRefreshReport,
      isLoadingHidden,
      setIsLoadingHidden,
      isSlicerLoading,
      setIsSlicerLoading,
      getReportsListForProject,
      updateGatewayDatasources,
      addReportRefresh,
      addReportScheduledUpdate,
      updateReportDatasources,
      shouldTestReportDatasourceConnections,
      setShouldTestReportDatasourceConnections
    }),
    [
      reports,
      setReports,
      selectedReport?.config.id,
      selectedReportId,
      setSelectedReport,
      embedToken,
      isTokenError,
      loadReports,
      saveAsReport,
      userCanUploadVisuals,
      setUserCanUploadVisuals,
      reportDataSuccessfullyUpdated,
      setReportDataSuccessfullyUpdated,
      selectedProject,
      setSelectedProject,
      isReportInEditionMode,
      setIsReportInEditionMode,
      isEditableReportSaveAsModalOpen,
      setIsEditableReportSaveAsModalOpen,
      reportSelectedPage,
      setReportSelectedPage,
      reportModule,
      setReportModule,
      isReportEditable,
      isDesktopLayout,
      sendAccountantDocumentRequest,
      shouldRegenerateToken,
      setShouldRegenerateToken,
      isSaveReportModalOpen,
      setIsSaveReportModalOpen,
      isAccountantDocumentRequestModalOpen,
      setAccountantDocumentRequestModalOpen,
      isReportLoaded,
      setIsReportLoaded,
      noProjectsSelectedBackdropOpen,
      setNoProjectsSelectedBackdropOpen,
      isReportExportTypeChoiceModalOpen,
      setIsReportExportTypeChoiceModalOpen,
      allReportVisuals,
      setAllReportVisuals,
      getAllReportVisuals,
      exportReportDataToFile,
      setReportSelectedFoldersSirens,
      reportSelectedFoldersSirens,
      canRefreshReport,
      setCanRefreshReport,
      isLoadingHidden,
      setIsLoadingHidden,
      isSlicerLoading,
      setIsSlicerLoading,
      getReportsListForProject,
      updateGatewayDatasources,
      addReportRefresh,
      addReportScheduledUpdate,
      updateReportDatasources,
      shouldTestReportDatasourceConnections,
      setShouldTestReportDatasourceConnections
    ]
  );

  return <ReportContext.Provider value={value}>{children}</ReportContext.Provider>;
};
ReportProvider.propTypes = {
  children: oneOfType([node, func]).isRequired
};

export default ReportProvider;
